import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import CreateChannelModal from "@/components/CreateChannelModal";
import { listSavedChannels } from "@/lib/api";
import { invalidateSavedChannels, useSavedChannelIds } from "@/hooks/useSavedChannels";

function nFmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const mins = Math.max(0, (Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${Math.round(mins)}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString();
}

export default function SavedNiches() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revChannel, setRevChannel] = useState(null);
  const { toggle } = useSavedChannelIds();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      invalidateSavedChannels();
      const data = await listSavedChannels();
      setRows(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load saved niches");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Group by niche label so a user with multiple "Bollywood Music" saves
  // sees them clustered.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = r.niche || "Unclassified";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    return Array.from(m.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [rows]);

  const remove = async (channel) => {
    try {
      await toggle({ channel_id: channel.channel_id });
      toast.success(`Removed · ${channel.niche || channel.name}`);
      setRows((prev) => prev.filter((p) => p.channel_id !== channel.channel_id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove");
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1300px] mx-auto" data-testid="saved-niches-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // PHASE 1 · SAVED NICHES
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        Your shortlist.
      </h1>
      <p className="text-zinc-300 text-sm max-w-2xl mb-8 leading-relaxed">
        Every channel you've starred from <b>Top Revenue</b> or{" "}
        <b>Top Earners</b>, grouped by niche. Click{" "}
        <span className="font-mono text-yellow-400">▸ Niche playbook</span> on
        any row to study its formula, then create your own channel kit for
        that niche.
      </p>

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">
          [recon] loading<span className="blink">_</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-zinc-800 rounded-sm p-10 text-center" data-testid="saved-empty">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // NO SAVES YET
          </div>
          <p className="text-zinc-300 text-sm max-w-md mx-auto mb-5">
            Head to <b>Top Revenue Channels</b> or <b>Top Earners</b> and hit
            the <span className="font-mono text-yellow-400">☆ Save</span>{" "}
            button on any row that looks promising. They'll show up here grouped
            by niche.
          </p>
        </div>
      ) : (
        <div className="space-y-10" data-testid="saved-groups">
          {grouped.map(([niche, list]) => (
            <section key={niche} data-testid={`saved-group-${niche}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <h2 className="font-display text-2xl font-black text-yellow-400 leading-none">
                  {niche}
                </h2>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                  {list.length} {list.length === 1 ? "channel" : "channels"}
                </span>
              </div>
              <div className="border border-zinc-800 rounded-sm overflow-hidden">
                <div className="grid grid-cols-12 px-4 py-3 bg-zinc-900 border-b-2 border-yellow-400/30 font-mono text-xs uppercase tracking-[0.15em] text-yellow-400 font-bold">
                  <div className="col-span-4">Channel</div>
                  <div className="col-span-2 text-center">Rev / mo</div>
                  <div className="col-span-1 text-center">Subs</div>
                  <div className="col-span-2 text-center">Saved</div>
                  <div className="col-span-2 text-center">Playbook</div>
                  <div className="col-span-1 text-center">Remove</div>
                </div>
                {list.map((c, i) => (
                  <div
                    key={c.channel_id}
                    data-testid={`saved-row-${c.channel_id}`}
                    className="grid grid-cols-12 px-4 py-4 border-b border-zinc-900 last:border-b-0 items-center hover:bg-zinc-950/60 transition-colors recon-row-in"
                    style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
                  >
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noreferrer"
                      className="col-span-4 flex items-center gap-3 min-w-0 hover:text-yellow-400"
                    >
                      {c.thumbnail ? (
                        <img src={c.thumbnail} alt={c.name} className="h-10 w-10 rounded-full shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-zinc-800 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm text-white font-semibold truncate">{c.name}</div>
                        <div className="text-xs text-zinc-400 truncate">{c.description}</div>
                      </div>
                    </a>
                    <div className="col-span-2 text-center font-display text-xl font-bold text-emerald-300">
                      ${nFmt(c.est_monthly_revenue)}
                    </div>
                    <div className="col-span-1 text-center text-sm font-mono text-zinc-100">
                      {nFmt(c.subscribers)}
                    </div>
                    <div className="col-span-2 text-center font-mono text-[11px] text-zinc-400">
                      {timeAgo(c.saved_at)}
                    </div>
                    <div className="col-span-2 text-center">
                      <button
                        data-testid={`saved-playbook-btn-${c.channel_id}`}
                        onClick={() => setRevChannel(c)}
                        className="font-mono text-[10px] uppercase tracking-[0.15em] text-yellow-400 border border-yellow-400/40 hover:bg-yellow-400 hover:text-black px-3 py-1.5 rounded-sm transition-colors leading-tight font-bold"
                      >
                        ▸ Niche playbook
                      </button>
                    </div>
                    <div className="col-span-1 text-center">
                      <button
                        data-testid={`saved-remove-btn-${c.channel_id}`}
                        onClick={() => remove(c)}
                        title="Remove from saved"
                        className="font-mono text-xs text-zinc-500 hover:text-red-400 px-2 py-1 transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <CreateChannelModal
        open={Boolean(revChannel)}
        channel={revChannel}
        onClose={() => setRevChannel(null)}
      />
    </div>
  );
}
